/**
 * Application routes.
 *
 * The client/internal split lives here:
 *
 *   /business-assessment/:token   the CLIENT. One route, no navigation out of
 *                                 it, and nothing under `internal/` rendered
 *                                 inside it.
 *   /  and  /internal/...         INTERNAL. Addressed by session id, which
 *                                 only an internal page ever has.
 *
 * There is exactly one public client route. The old `/interview/:token` is
 * gone rather than redirected: a second public entrance would be a second
 * client experience to keep honest, and the link a client is sent is issued
 * by the backend, which now issues only this one.
 *
 * Route separation is a matter of what gets rendered, not of security: the
 * backend refuses internal data to a client token regardless of what the
 * browser asks for.
 */

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import BusinessAssessmentPage from './client/BusinessAssessmentPage';
import SessionsPage from './internal/SessionsPage';
import DiscoveriesPage from './internal/DiscoveriesPage';
import ReportPage from './internal/ReportPage';
import TranscriptPage from './internal/TranscriptPage';
import DiscoveryPage from './internal/DiscoveryPage';
import NotFoundPage from './pages/NotFoundPage';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public Client Assessment Route */}
          <Route path="/business-assessment/:token" element={<BusinessAssessmentPage />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/api/auth/callback" element={<AuthCallbackPage />} />

          {/* Protected Internal Routes */}
          <Route path="/" element={<ProtectedRoute><SessionsPage /></ProtectedRoute>} />
          <Route path="/internal/discoveries" element={<ProtectedRoute><DiscoveriesPage /></ProtectedRoute>} />
          <Route path="/internal/sessions/:sessionId/report" element={<ProtectedRoute><ReportPage /></ProtectedRoute>} />
          <Route path="/internal/sessions/:sessionId/transcript" element={<ProtectedRoute><TranscriptPage /></ProtectedRoute>} />
          <Route path="/internal/sessions/:sessionId/discovery" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />

          <Route path="/404" element={<NotFoundPage />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
